import { Link } from "react-router-dom";
import { ArrowRight, Award, Clock, ShieldCheck, MapPin, BadgeCheck, UserCheck, Hammer, Droplets, Layers, Wrench, Building2, ClipboardCheck, Phone, Mail } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { SERVICES } from "@/data/services";
import { SITE } from "@/config/site";

const ICONS = { Hammer, Droplets, Layers, Wrench, Building2, ClipboardCheck };

export const Reassurance = () => {
  const items = [
    { icon: Award, t: "Entreprise artisanale inscrite à la CMA" },
    { icon: Clock, t: "Devis gratuit et détaillé sous 48 h ouvrées" },
    { icon: ShieldCheck, t: "Assurance décennale et RC professionnelle" },
    { icon: MapPin, t: "Intervention dans toute l'Île-de-France" },
    { icon: BadgeCheck, t: "Travaux garantis : 1 an, 2 ans, 10 ans" },
    { icon: UserCheck, t: "Interlocuteur unique du début à la fin" },
  ];
  return (
    <section className="bg-white border-b border-line" data-testid="reassurance-section">
      <Stagger className="container-x grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 divide-x divide-line">
        {items.map(({ icon: Icon, t }) => (
          <StaggerItem key={t} className="px-4 py-8 flex flex-col gap-3 items-start">
            <Icon className="text-copper" size={26} strokeWidth={1.75} />
            <p className="text-sm font-semibold text-slate leading-snug">{t}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
};

export const Presentation = () => (
  <section className="section bg-concrete" data-testid="presentation-section">
    <div className="container-x grid lg:grid-cols-12 gap-12">
      <Reveal className="lg:col-span-5">
        <p className="eyebrow mb-5">Qui sommes-nous</p>
        <h2 className="h-display text-4xl sm:text-5xl">Un artisan du bâtiment, <span className="text-copper">360°</span> sur vos travaux</h2>
      </Reveal>
      <div className="lg:col-span-7 space-y-6 text-ink/85 leading-relaxed text-base sm:text-lg">
        <Reveal delay={0.1}><p><strong className="text-slate">Qui nous sommes.</strong> O'RIZON 360 est une {SITE.legalFormShort} au capital de {SITE.capital}, immatriculée au RCS de {SITE.rcsCity} le {SITE.registrationDate}, dont l'activité a débuté le {SITE.activityStartDate}. Entreprise artisanale, nous sommes inscrits auprès de la Chambre des Métiers et de l'Artisanat.</p></Reveal>
        <Reveal delay={0.15}><p><strong className="text-slate">Ce que nous faisons.</strong> Travaux de rénovation et d'entretien d'immeubles : maçonnerie, étanchéité, isolation et installation d'équipements techniques, pour des logements, des locaux professionnels et des parties communes.</p></Reveal>
        <Reveal delay={0.2}><p><strong className="text-slate">Notre approche.</strong> Un travail soigné, le respect des délais annoncés et des devis transparents. Nous accompagnons aussi bien les particuliers que les professionnels : syndics, bailleurs, agences et promoteurs.</p></Reveal>
        <Reveal delay={0.25}><p><strong className="text-slate">Notre zone d'intervention.</strong> Nogent-sur-Marne, le Val-de-Marne et toute l'Île-de-France.</p></Reveal>
        <Reveal delay={0.3}><Link to="/a-propos" className="inline-flex items-center gap-2 font-semibold text-copper hover:gap-3 transition-[gap]" data-testid="presentation-about-link">En savoir plus sur l'entreprise <ArrowRight size={18} /></Link></Reveal>
      </div>
    </div>
  </section>
);

export const ServicesPreview = () => (
  <section className="section bg-white" data-testid="services-preview-section">
    <div className="container-x">
      <Reveal className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
        <div>
          <p className="eyebrow mb-5">Nos prestations</p>
          <h2 className="h-display text-4xl sm:text-5xl">Six métiers, <span className="text-copper">un seul interlocuteur</span></h2>
        </div>
        <Link to="/services" className="btn-outline" data-testid="services-preview-all-link">Toutes nos prestations <ArrowRight size={18} /></Link>
      </Reveal>
      <Stagger className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          return (
            <StaggerItem key={s.slug}>
              <Link to={`/services#${s.slug}`} className="card card-hover group block p-7 h-full relative overflow-hidden" data-testid={`service-card-${s.slug}`}>
                <span className="absolute top-5 right-6 font-heading font-extrabold text-5xl text-concrete group-hover:text-copper/15 transition-colors">0{i + 1}</span>
                <span className="w-14 h-14 rounded-xl bg-copper-soft text-copper flex items-center justify-center group-hover:bg-copper group-hover:text-white transition-colors duration-300"><Icon size={26} strokeWidth={1.75} /></span>
                <h3 className="font-heading font-bold uppercase text-2xl text-slate mt-6">{s.title}</h3>
                <p className="mt-3 text-muted leading-relaxed">{s.short}</p>
                <span className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-copper">Découvrir <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" /></span>
              </Link>
            </StaggerItem>
          );
        })}
      </Stagger>
    </div>
  </section>
);

export const Method = () => {
  const steps = [
    { t: "Prise de contact et visite technique", d: "Vous nous décrivez votre projet. Si nécessaire, nous organisons une visite sur place pour mesurer, diagnostiquer et conseiller." },
    { t: "Devis détaillé, gratuit et sans engagement", d: "Un devis clair, poste par poste, avec le taux de TVA applicable, les délais et les modalités. Valable 30 jours." },
    { t: "Réalisation des travaux dans les règles de l'art", d: "Planning respecté, chantier propre et sécurisé, point régulier avec vous. Tout travail supplémentaire fait l'objet d'un devis complémentaire." },
    { t: "Réception, garanties et suivi", d: "Procès-verbal de réception, levée des éventuelles réserves, remise des garanties légales (1 an, 2 ans, 10 ans) et suivi dans le temps." },
  ];
  return (
    <section className="section bg-slate text-white grain" data-testid="method-section">
      <div className="container-x relative z-10">
        <Reveal className="max-w-2xl mb-16">
          <p className="eyebrow mb-5">Notre méthode</p>
          <h2 className="h-display !text-white text-4xl sm:text-5xl">Quatre étapes, <span className="text-copper">zéro surprise</span></h2>
        </Reveal>
        <Stagger className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden">
          {steps.map((s, i) => (
            <StaggerItem key={s.t} className="bg-slate p-8 hover:bg-petrol transition-colors duration-300">
              <span className="font-heading font-extrabold text-6xl text-copper leading-none">0{i + 1}</span>
              <h3 className="font-heading font-bold uppercase text-xl mt-6 leading-tight">{s.t}</h3>
              <p className="mt-4 text-white/65 text-sm leading-relaxed">{s.d}</p>
            </StaggerItem>
          ))}
        </Stagger>
      </div>
    </section>
  );
};

export const Commitments = () => {
  const items = [
    { big: "48 h", s: "ouvrées pour recevoir votre devis" },
    { big: "1 jour", s: "ouvré pour une première réponse" },
    { big: "8 dép.", s: "toute l'Île-de-France couverte" },
    { big: "0 €", s: "devis gratuit et sans engagement" },
  ];
  return (
    <section className="bg-copper text-white" data-testid="commitments-section">
      <Stagger className="container-x grid grid-cols-2 lg:grid-cols-4 py-14 gap-y-10">
        {items.map((i) => (
          <StaggerItem key={i.big} className="pr-6">
            <p className="font-heading font-extrabold text-5xl sm:text-6xl leading-none">{i.big}</p>
            <p className="mt-3 text-white/85 text-sm sm:text-base font-medium">{i.s}</p>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
};

export const FinalCta = () => (
  <section className="section bg-white" data-testid="final-cta-section">
    <div className="container-x">
      <Reveal className="rounded-3xl bg-slate text-white p-10 sm:p-16 lg:p-20 relative overflow-hidden grain">
        <span className="absolute -right-20 -top-20 w-80 h-80 rounded-full border-[3px] border-copper/30" aria-hidden="true" />
        <span className="absolute right-10 bottom-[-60px] w-60 h-60 rounded-full bg-copper/10" aria-hidden="true" />
        <div className="relative z-10 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="eyebrow mb-5">Parlons de votre projet</p>
            <h2 className="h-display !text-white text-4xl sm:text-5xl lg:text-6xl">Un projet de rénovation ? <span className="text-copper">Parlons-en.</span></h2>
          </div>
          <div className="flex flex-col gap-4 lg:items-end">
            <Link to="/contact" className="btn-primary w-full sm:w-auto" data-testid="final-cta-devis-button">Demander un devis gratuit <ArrowRight size={18} /></Link>
            <a href={SITE.phone.href} className="inline-flex items-center gap-3 text-xl font-heading font-bold hover:text-copper transition-colors" data-testid="final-cta-phone"><Phone size={20} className="text-copper" /> {SITE.phone.display}</a>
            <a href={`mailto:${SITE.email}`} className="inline-flex items-center gap-3 text-white/75 hover:text-copper transition-colors" data-testid="final-cta-email"><Mail size={18} className="text-copper" /> {SITE.email}</a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);
